class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.trendingAndProductsBlock = this.querySelector('[data-quick-trending-products]');
    this.closeStickySearchButton = document.querySelector('.header-search-close');
    this.searchDetails = this.querySelector('.search_details');
    this.predictiveSearch = this.querySelector('predictive-search');
    this.isOpen = false;
    this.quickSearchTrending = this.querySelectorAll('.quickSearchTrending .link');
    this.relatedTitle = this.dataset.relatedTitle || 'Top Search Terms';
    this.trendingKeywords = this.parseTrendingKeywords();
    this.productToShow = Math.max(parseInt(this.dataset.productToShow || '6', 10) || 6, 6);

    this.setupEventListeners();
    this.quickSearchTrending.forEach((element) => {
      element.addEventListener('click', this.quickSearchTrendingClick.bind(this));
    });
  }

  parseTrendingKeywords() {
    try {
      const raw = this.dataset.trendingKeywords || '[]';
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch (error) {
      return [];
    }
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener(
      'input',
      debounce((event) => {
        this.onChange(event);
      }, 500).bind(this)
    );
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    document.addEventListener('click', this.onDocClick.bind(this));
    if (this.closeStickySearchButton != null) {
      this.closeStickySearchButton.addEventListener('click', this.onCloseStickySearchClick.bind(this));
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    const query = this.getQuery();
    if (!query.length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }
    // Scale / "part for 1/18" needs tag filters — navigate with built URL instead of bare form q.
    event.preventDefault();
    window.location.href = this.buildSearchResultsUrl(query);
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return this.showTrendingAndProducts();

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
      this.showTrendingAndProducts();
    }
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  /** Shopify storefront search: trailing * enables partial/prefix word match (e.g. refle* → Reflex). */
  toPartialSearchQuery(searchTerm) {
    const term = String(searchTerm || '').trim();
    if (!term) return term;
    if (term === '*') return term;
    if (/\*$/.test(term)) return term;
    // Keep multi-word queries usable: only expand the last token.
    const parts = term.split(/\s+/);
    const last = parts[parts.length - 1];
    if (last.length < 2 || last === '*') return term;
    parts[parts.length - 1] = last + '*';
    return parts.join(' ');
  }

  /**
   * Scale-aware search: "1/18", "1:10", "part for 1/18", "parts 1/10 furitek"
   * → filter.p.tag=rcf4|{scale} (+ rcf1|Car Parts when part intent).
   *
   * Intent words (part/for/kit…) are stripped from q so Shopify matches brand/model
   * text, but partIntent ALWAYS adds the Car Parts tag filter — including when there
   * is no scale (fixes "part for hobbyplus" only searching "hobbyplus").
   */
  parseScaleAwareQuery(searchTerm) {
    const original = String(searchTerm || '').trim();
    let work = original
      .replace(/\b1\s*:\s*(\d{1,3})\b/gi, '1/$1')
      .replace(/\b1\s*-\s*(\d{1,3})\b/gi, '1/$1');

    const scaleMatch = work.match(/\b1\/(\d{1,3})\b/);
    const scale = scaleMatch ? `1/${scaleMatch[1]}` : null;
    const partIntent =
      /\b(parts?|bod(?:y|ies)|shells?|bumpers?|tires?|wheels?)\b/i.test(work);
    const kitIntent = /\bkits?\b/i.test(work) || /-kit\b/i.test(work) || /\brtrs?\b/i.test(work);

    const tags = [];
    if (scale) tags.push(`rcf4|${scale}`);
    // Always scope to Car Parts when the shopper asks for parts/body/shell — not only with scale
    if (partIntent) tags.push('rcf1|Car Parts');

    let q = work
      .replace(/\b1\/\d{1,3}\b/gi, ' ')
      // Strip filler + intent words from text query (intent is carried by tag filter)
      .replace(/\b(parts?|for|compatible|compat|fits?|fitting|scale|sized?)\b/gi, ' ')
      .replace(/\b(kits?|rtrs?)\b/gi, ' ')
      .replace(/-kit\b/gi, ' ')
      .replace(/[^\w\s/+*&-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    // Keep body/shell in q — they are real product-type keywords in titles (Lexan Body…)
    // Only "parts?"/"for" are stripped above; body stays.

    if (!q && tags.length) {
      q = '*';
    } else if (q) {
      q = this.toPartialSearchQuery(q);
    } else {
      q = this.toPartialSearchQuery(original);
    }

    return { q, tags, scale, partIntent, kitIntent, original };
  }

  buildSearchResultsUrl(searchTerm, opts = {}) {
    const parsed = this.parseScaleAwareQuery(searchTerm);
    const params = new URLSearchParams();
    params.set('q', parsed.q);
    params.set('type', 'product');
    params.set('options[prefix]', 'last');
    params.set('sort_by', 'relevance');
    parsed.tags.forEach((tag) => params.append('filter.p.tag', tag));
    // Persist intent for Liquid ranking when intent words were stripped from q
    if (parsed.partIntent) params.set('drc_intent', 'part');
    else if (parsed.kitIntent) params.set('drc_intent', 'kit');
    if (opts.sectionId) params.set('section_id', opts.sectionId);

    return `${routes.search_url}?${params.toString()}`;
  }

  buildBestsellingSearchUrl(searchTerm) {
    return this.buildSearchResultsUrl(searchTerm, { sectionId: 'predictive-search-bestselling' });
  }

  buildQuerySuggestUrl(searchTerm) {
    const params = new URLSearchParams();
    params.set('q', searchTerm);
    params.set('resources[type]', 'query');
    params.set('resources[limit]', '12');
    return `${routes.predictive_search_url}.json?${params.toString()}`;
  }

  normalizeTerm(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9\s/+&-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  highlightTerm(term, searchTerm) {
    const safeTerm = this.escapeHtml(term);
    const query = this.escapeHtml(searchTerm).trim();
    if (!query) return safeTerm;
    try {
      const pattern = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig');
      return safeTerm.replace(pattern, '<strong>$1</strong>');
    } catch (error) {
      return safeTerm;
    }
  }

  extractTermsFromProducts(searchTerm, root) {
    const query = this.normalizeTerm(searchTerm);
    if (!query || !root) return [];
    const titles = Array.from(root.querySelectorAll('[data-product-title]'))
      .map((node) => node.getAttribute('data-product-title') || '')
      .filter(Boolean);

    const terms = [];
    titles.forEach((title) => {
      const normalizedTitle = this.normalizeTerm(title);
      if (!normalizedTitle.includes(query)) return;

      // Prefer compact phrases that start with / contain the query (Amain-style)
      const words = normalizedTitle.split(' ').filter(Boolean);
      const startIndex = words.findIndex((word) => word.includes(query.split(' ')[0]));
      if (startIndex === -1) return;

      for (let len = 2; len <= Math.min(5, words.length - startIndex); len += 1) {
        const phrase = words.slice(startIndex, startIndex + len).join(' ');
        if (phrase.length >= query.length + 2) terms.push(phrase);
      }

      // Also add vendor-like first token + next model token
      if (words.length >= 2) {
        terms.push(`${words[0]} ${words[1]}`);
      }
    });

    return terms;
  }

  buildRelatedSearches(searchTerm, querySuggestions, productTerms) {
    const normalizedQuery = this.normalizeTerm(searchTerm);
    const tokens = normalizedQuery.split(' ').filter((token) => token.length > 1);
    const related = [];
    const seen = new Set([normalizedQuery]);

    const pushUnique = (term) => {
      const clean = String(term || '').trim();
      if (!clean) return;
      const key = this.normalizeTerm(clean);
      if (!key || seen.has(key) || key === normalizedQuery) return;
      // Prefer terms that still relate to the typed query
      if (tokens.length && !tokens.some((token) => key.includes(token))) return;
      seen.add(key);
      related.push(clean);
    };

    // Always keep the typed query as first suggestion (like Amain seed)
    if (normalizedQuery) {
      related.push(searchTerm.trim());
      seen.add(normalizedQuery);
    }

    (querySuggestions || []).forEach((item) => {
      pushUnique(item && (item.text || item.query || item));
    });

    (productTerms || []).forEach(pushUnique);

    this.trendingKeywords.forEach((keyword) => {
      const normalizedKeyword = this.normalizeTerm(keyword);
      if (!normalizedKeyword) return;
      const matches =
        normalizedKeyword.includes(normalizedQuery) ||
        tokens.some((token) => normalizedKeyword.includes(token));
      if (matches) pushUnique(keyword);
    });

    if (tokens.includes('servo') || normalizedQuery === 'servo') {
      ['servos', 'digital servo', 'brushless servo', 'micro servo', 'torq servo'].forEach(pushUnique);
    }
    if (tokens.includes('esc')) {
      ['brushless esc', 'brushed esc', 'esc combo'].forEach(pushUnique);
    }
    if (tokens.includes('lipo') || tokens.includes('battery')) {
      ['lipo battery', '2s lipo', '3s lipo', 'battery charger'].forEach(pushUnique);
    }
    if (tokens.includes('drift')) {
      ['drift car', 'drift tires', 'rwd drift', 'on road drift'].forEach(pushUnique);
    }
    if (tokens.includes('losi')) {
      ['losi mini', 'losi buggy', 'losi short course', 'losi 22'].forEach(pushUnique);
    }

    // Sort: keep exact query first, then terms starting with query, then shorter phrases
    const exact = this.normalizeTerm(searchTerm);
    return related
      .sort((a, b) => {
        const na = this.normalizeTerm(a);
        const nb = this.normalizeTerm(b);
        if (na === exact && nb !== exact) return -1;
        if (nb === exact && na !== exact) return 1;
        const aStarts = na.startsWith(normalizedQuery) ? 0 : 1;
        const bStarts = nb.startsWith(normalizedQuery) ? 0 : 1;
        if (aStarts !== bStarts) return aStarts - bStarts;
        return a.length - b.length;
      })
      .slice(0, 16);
  }

  injectRelatedSearches(root, searchTerm, relatedTerms) {
    const wrap = root.querySelector('[data-related-searches]');
    const list = root.querySelector('[data-related-searches-list]');
    if (!wrap || !list) return;

    list.innerHTML = '';
    if (!relatedTerms.length) {
      wrap.hidden = true;
      wrap.classList.remove('is-visible');
      return;
    }

    const title = wrap.querySelector('.qs-amain__terms-title .text, .predictive-search__related-title .text');
    if (title) title.textContent = this.relatedTitle;

    relatedTerms.forEach((term) => {
      const li = document.createElement('li');
      li.className = 'qs-amain__terms-item';
      const link = document.createElement('a');
      link.className = 'qs-amain__terms-link';
      link.href = this.buildSearchResultsUrl(term);
      link.innerHTML = this.highlightTerm(term, searchTerm);
      li.appendChild(link);
      list.appendChild(li);
    });
    wrap.hidden = false;
    wrap.classList.add('is-visible');
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      this.updateViewAllLink(searchTerm);
      return;
    }

    const productsUrl = this.buildBestsellingSearchUrl(searchTerm);
    const queriesUrl = this.buildQuerySuggestUrl(searchTerm);

    Promise.all([
      fetch(productsUrl).then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      }),
      fetch(queriesUrl)
        .then((response) => (response.ok ? response.json() : { resources: { results: { queries: [] } } }))
        .catch(() => ({ resources: { results: { queries: [] } } }))
    ])
      .then(([html, suggestJson]) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const section =
          doc.querySelector('#shopify-section-predictive-search-bestselling') ||
          doc.querySelector('[id^="shopify-section-"]');
        const resultsMarkup = section ? section.innerHTML : '';

        const shell = document.createElement('div');
        shell.innerHTML = resultsMarkup;

        const productTerms = this.extractTermsFromProducts(searchTerm, shell);
        const querySuggestions =
          (suggestJson &&
            suggestJson.resources &&
            suggestJson.resources.results &&
            suggestJson.resources.results.queries) ||
          [];
        const relatedTerms = this.buildRelatedSearches(searchTerm, querySuggestions, productTerms);
        this.injectRelatedSearches(shell, searchTerm, relatedTerms);
        const finalMarkup = shell.innerHTML;

        this.cachedResults[queryKey] = finalMarkup;
        this.renderSearchResults(finalMarkup);
        this.updateViewAllLink(searchTerm);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setAttribute('open', true);
    this.setLiveRegionResults();
    this.open();
    this.hideTrendingAndProducts();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const liveRegion = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegion) this.setLiveRegionText(liveRegion.textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('[class^="header-navigation"]').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }

  showTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) return;
    this.trendingAndProductsBlock.classList.add('is-show');
    this.trendingAndProductsBlock.classList.remove('hidden');
  }

  hideTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) return;

    this.trendingAndProductsBlock.classList.remove('is-show');
    this.trendingAndProductsBlock.classList.add('hidden');
  }

  onDocClick(e) {
    const isInModal = this.contains(e.target);
    const $target = e.target;

    if (
      !isInModal ||
      $target.closest('.header-search-popup-close') ||
      $target.matches('.header-search-popup-close') ||
      $target.closest('.header-search-close') ||
      $target.matches('.header-search-close')
    ) {
      this.close();
      this.hideTrendingAndProducts();
    }
  }

  onCloseStickySearchClick() {
    this.close();
    this.hideTrendingAndProducts();
    document.querySelector('body').classList.remove('sticky-search-open');
    document.querySelector('body').classList.remove('sticky-search-menu-open');
    this.closest('[class*="section-header-"]')?.classList.remove('sticky-search-menu-open');
    this.closest('[class*="section-header-"]')?.classList.remove('sticky-search-menu-custom-open');
  }

  updateViewAllLink(searchTerm) {
    const qsViewAllLink = document.querySelector('[data-qs-view-all-link]');
    if (!qsViewAllLink) return;

    const linkTotal = this.buildSearchResultsUrl(searchTerm);

    qsViewAllLink.href = linkTotal;
    return this.getTotalResults(linkTotal).then((count) => {
      qsViewAllLink.innerHTML = qsViewAllLink.innerHTML.replace('()', `(${count})`);
    });
  }

  getTotalResults(url) {
    return fetch(url)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const section = new DOMParser().parseFromString(text, 'text/html').querySelector('[id^="SearchSection"]');
        return section ? section.dataset.searchCount : '0';
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  quickSearchTrendingClick(event) {
    window.location = event.currentTarget.getAttribute('href');
  }
}

customElements.define('predictive-search', PredictiveSearch);

// Upgrade bare search forms + legacy URLs (scale / "part for 1/18") to tag-filtered search.
(function enhanceScaleAwareSearch() {
  function getHelper() {
    const el = document.querySelector('predictive-search');
    if (el && typeof el.parseScaleAwareQuery === 'function') return el;
    // Fallback instance methods via prototype if element not ready
    return PredictiveSearch.prototype;
  }

  function buildUrlFromQuery(query) {
    const helper = getHelper();
    if (helper.buildSearchResultsUrl) {
      return helper.buildSearchResultsUrl.call(helper, query);
    }
    return null;
  }

  function maybeRedirectSearchPage() {
    if (!window.location.pathname || window.location.pathname.indexOf('/search') !== 0) return;
    const params = new URLSearchParams(window.location.search);
    const q = params.get('q');
    if (!q || params.has('filter.p.tag')) return;

    const helper = getHelper();
    const parsed = helper.parseScaleAwareQuery
      ? helper.parseScaleAwareQuery.call(helper, q.replace(/\*$/, ''))
      : null;
    if (!parsed || !parsed.tags.length) return;

    const next = buildUrlFromQuery(q.replace(/\*$/, ''));
    if (!next || next === window.location.pathname + window.location.search) return;

    window.location.replace(next);
  }

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches('form.search, form[action*="/search"]')) return;
      if (form.closest('predictive-search')) return; // handled by component

      const input = form.querySelector('input[type="search"], input[name="q"]');
      if (!input || !input.value.trim()) return;

      const next = buildUrlFromQuery(input.value.trim());
      if (!next) return;
      event.preventDefault();
      window.location.href = next;
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', maybeRedirectSearchPage);
  } else {
    maybeRedirectSearchPage();
  }
})();

